import { useEffect, useRef, type RefObject } from "react"
import { ICONS } from "./Icons"
import {
	INJECT_LABEL,
	INJECT_ICON,
	LOADING_MESSAGES,
	MESSAGE_INTERVAL_MS,
	SPAWN_INTERVAL_MS,
	MAX_CONCURRENT_ITEMS,
	TRAVEL_MS,
	INJECT_ACCENT,
} from "./Steps"
import { InjectKey } from "./Types"
import * as s from "./ItineraryLoader.css"

export type FlowDirection = "inward" | "outward"

const N = 4200
const GOLDEN = Math.PI * (3 - Math.sqrt(5))
const ITEM_KEYS = Object.keys(INJECT_LABEL) as InjectKey[]

interface Point {
	x: number
	y: number
	z: number
	size: number
	c: [number, number, number]
}

function makePoints(): Point[] {
	const pts: Point[] = []
	for (let i = 0; i < N; i++) {
		const y = 1 - (i / (N - 1)) * 2
		const r = Math.sqrt(1 - y * y)
		const th = GOLDEN * i
		const rr = Math.random()
		let c: [number, number, number]
		if (rr < 0.52) c = [75, 94, 228]
		else if (rr < 0.8) c = [46, 62, 201]
		else if (rr < 0.95) c = [109, 139, 255]
		else c = [169, 204, 255]
		pts.push({
			x: Math.cos(th) * r,
			y,
			z: Math.sin(th) * r,
			size: 1 + Math.random() * 0.9,
			c,
		})
	}
	return pts
}

function easeInOutCubic(t: number) {
	return t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2
}

interface FlyItem {
	el: HTMLDivElement
	spawnedAt: number
	originX: number
	originY: number
	destX: number
	destY: number
	sizeVariance: number
	wobbleAmp: number
	wobbleSign: number
	travelMs: number
}

export interface EngineRefs {
	stage: RefObject<HTMLDivElement>
	canvas: RefObject<HTMLCanvasElement>
	live: RefObject<HTMLSpanElement>
}

export function useItineraryLoaderEngine({
	loading,
	direction = "inward",
}: {
	loading: boolean
	direction?: FlowDirection
}): EngineRefs {
	const stage = useRef<HTMLDivElement>(null)
	const canvas = useRef<HTMLCanvasElement>(null)
	const live = useRef<HTMLSpanElement>(null)

	useEffect(() => {
		if (!loading) return

		const stageEl = stage.current
		const canvasEl = canvas.current
		const liveEl = live.current
		if (!stageEl || !canvasEl || !liveEl) return

		const ctx = canvasEl.getContext("2d")
		if (!ctx) return

		const refs = {
			stageEl,
			canvasEl,
			liveEl,
			ctx,
		}

		const reduce = window.matchMedia("(prefers-reduced-motion: reduce)").matches
		const points = makePoints()

		let width = 0,
			height = 0,
			centerX = 0,
			centerY = 0,
			radius = 0,
			dpr = 1

		function resize() {
			const { stageEl, canvasEl, ctx } = refs
			dpr = Math.min(window.devicePixelRatio || 1, 2)
			const rect = stageEl.getBoundingClientRect()
			width = rect.width
			height = rect.height
			canvasEl.width = width * dpr
			canvasEl.height = height * dpr
			ctx.setTransform(dpr, 0, 0, dpr, 0, 0)
			centerX = width / 2
			centerY = height / 2
			radius = Math.min(width, height) * 0.5
		}
		resize()
		window.addEventListener("resize", resize)

		// ---- decorative fly items, drawn from real component types ----
		const activeItems: FlyItem[] = []
		let itemCursor = 0
		let lastAngle: number | null = null

		function nextAngle(): number {
			// bias away from the previous spawn angle so items don't clump on one side
			let angle = Math.random() * Math.PI * 2
			if (lastAngle !== null) {
				const diff = Math.abs(angle - lastAngle)
				const wrapped = Math.min(diff, Math.PI * 2 - diff)
				if (wrapped < Math.PI * 0.5) {
					angle = lastAngle + Math.PI * 0.75 + Math.random() * Math.PI * 0.5
				}
			}
			lastAngle = angle
			return angle
		}

		function spawnItem() {
			const { stageEl } = refs
			if (activeItems.length >= MAX_CONCURRENT_ITEMS) return

			const key = ITEM_KEYS[itemCursor % ITEM_KEYS.length]
			itemCursor++

			const angle = nextAngle()
			const edgeR = radius * 2.1
			const edgeX = centerX + Math.cos(angle) * edgeR
			const edgeY = centerY + Math.sin(angle) * edgeR

			const accent = INJECT_ACCENT[key]
			const el = document.createElement("div")
			el.className = `${s.chip} ${s.chipSpawning}`
			el.style.setProperty("--chip-accent", accent)
			el.style.setProperty("--chip-icon-bg", hexToRgba(accent, 0.16))
			el.style.setProperty("--chip-border", hexToRgba(accent, 0.35))
			el.style.setProperty("--chip-glow", hexToRgba(accent, 0.55))
			el.innerHTML = `<span class="${s.chipIco}">${ICONS[INJECT_ICON[key]]}</span>${INJECT_LABEL[key]}`
			el.style.opacity = "0"
			stageEl.appendChild(el)

			// drop the pop-in animation class once it's played, so later transform
			// updates (position/scale) aren't fighting a looping CSS animation
			window.setTimeout(() => el.classList.remove(s.chipSpawning), 620)

			const [originX, originY, destX, destY] =
				direction === "inward"
					? [edgeX, edgeY, centerX, centerY]
					: [centerX, centerY, edgeX, edgeY]

			// slight per-item size variance so they don't all read as identical stamps
			const sizeVariance = 0.9 + Math.random() * 0.25
			// small random wobble range, so rotation direction/amount differs per item
			const wobbleAmp = 4 + Math.random() * 5
			const wobbleSign = Math.random() < 0.5 ? -1 : 1

			activeItems.push({
				el,
				spawnedAt: performance.now(),
				originX,
				originY,
				destX,
				destY,
				sizeVariance,
				wobbleAmp,
				wobbleSign,
				travelMs: TRAVEL_MS + Math.random() * 600,
			})
		}

		// small helper — put near the top of the file
		function hexToRgba(hex: string, alpha: number): string {
			const n = parseInt(hex.slice(1), 16)
			const r = (n >> 16) & 255,
				g = (n >> 8) & 255,
				b = n & 255
			return `rgba(${r},${g},${b},${alpha})`
		}

		const spawnTimer = window.setInterval(spawnItem, SPAWN_INTERVAL_MS)
		spawnItem()

		function updateFlyItems(now: number) {
			for (let i = activeItems.length - 1; i >= 0; i--) {
				const it = activeItems[i]
				const t = Math.min((now - it.spawnedAt) / it.travelMs, 1)
				const eased = easeInOutCubic(t)

				const x = it.originX + (it.destX - it.originX) * eased
				const y = it.originY + (it.destY - it.originY) * eased

				const baseScale =
					direction === "inward" ? 1 - eased * 0.82 : 0.18 + eased * 0.82
				const scale = baseScale * it.sizeVariance

				// wobble settles out as it nears its destination — lively at launch,
				// calm on arrival, rather than spinning the whole way
				const settle = 1 - eased
				const rotate =
					Math.sin(t * Math.PI * 2.4) * it.wobbleAmp * settle * it.wobbleSign

				const fadeIn = Math.min(t / 0.18, 1)
				const fadeOut = Math.min((1 - t) / 0.18, 1)
				const opacity = Math.min(fadeIn, fadeOut)

				it.el.style.transform = `translate(${x}px, ${y}px) translate(-50%, -50%) rotate(${rotate}deg) scale(${scale})`
				it.el.style.opacity = String(opacity)

				if (t >= 1) {
					it.el.remove()
					activeItems.splice(i, 1)
				}
			}
		}

		// ---- orb ----
		let animationStart = performance.now()
		let animationFrame = 0

		function frame(now: number) {
			const { ctx } = refs
			ctx.clearRect(0, 0, width, height)
			const elapsed = now - animationStart
			const baseRadius = radius
			const shrinkProgress =
				direction === "outward" ? Math.min(elapsed / 18000, 1) : 0
			const scale =
				direction === "outward"
					? 0.55 + 0.45 * (1 - easeInOutCubic(shrinkProgress))
					: 1

			const glowRadius = baseRadius * scale

			const yaw = elapsed * 0.00024 + 0.6
			const pitch = 0.42
			const cosYaw = Math.cos(yaw),
				sinYaw = Math.sin(yaw)
			const cosPitch = Math.cos(pitch),
				sinPitch = Math.sin(pitch)

			const breathe = 0.5 + 0.5 * Math.sin(elapsed * 0.0012)
			const glow = 0.22 + breathe * 0.18

			const gradient = ctx.createRadialGradient(
				centerX,
				centerY,
				glowRadius * 0.05,
				centerX,
				centerY,
				glowRadius * 1.05
			)
			gradient.addColorStop(0, `rgba(90,120,255,${glow * 0.9})`)
			gradient.addColorStop(0.55, `rgba(60,80,220,${glow * 0.5})`)
			gradient.addColorStop(1, "rgba(40,55,180,0)")
			ctx.fillStyle = gradient
			ctx.beginPath()
			ctx.arc(centerX, centerY, glowRadius * 1.05, 0, Math.PI * 2)
			ctx.fill()

			for (const point of points) {
				const x = point.x * cosYaw - point.z * sinYaw
				let z = point.x * sinYaw + point.z * cosYaw
				const y = point.y * cosPitch - z * sinPitch
				z = point.y * sinPitch + z * cosPitch

				const perspective = 1 / (1.9 - z * 0.6)
				const screenX = centerX + x * baseRadius * perspective
				const screenY = centerY + y * baseRadius * perspective

				const drawX = centerX + (screenX - centerX) * scale
				const drawY = centerY + (screenY - centerY) * scale

				const depth = (z + 1) / 2
				const edge = Math.sqrt(x * x + y * y)
				const rim = Math.max(0, (edge - 0.72) / 0.28)
				const rimGlow = rim * rim

				const alpha =
					Math.min((0.3 + 0.7 * depth) * (0.55 + 0.85 * rimGlow), 1) *
					(0.85 + 0.25 * breathe)
				if (alpha <= 0.01) continue

				const size = point.size * (0.7 + 0.6 * depth) * (1 + rimGlow) * scale
				ctx.globalAlpha = alpha
				ctx.fillStyle = `rgb(${point.c[0]},${point.c[1]},${point.c[2]})`
				ctx.fillRect(drawX - size / 2, drawY - size / 2, size, size)
			}
			ctx.globalAlpha = 1

			updateFlyItems(now)
			animationFrame = requestAnimationFrame(frame)
		}

		let msgIndex = 0

		const randomMessage =
			LOADING_MESSAGES[Math.floor(Math.random() * LOADING_MESSAGES.length)]

		liveEl.textContent = randomMessage

		const msgTimer = window.setInterval(() => {
			const randomMessage =
				LOADING_MESSAGES[Math.floor(Math.random() * LOADING_MESSAGES.length)]

			msgIndex = (msgIndex + 1) % LOADING_MESSAGES.length

			liveEl.style.opacity = "0"

			window.setTimeout(() => {
				if (!live.current) return

				live.current.textContent = randomMessage
				live.current.style.opacity = "1"
			}, 160)
		}, MESSAGE_INTERVAL_MS)

		if (!reduce) {
			animationStart = performance.now()
			animationFrame = requestAnimationFrame(frame)
		}

		return () => {
			cancelAnimationFrame(animationFrame)
			window.clearInterval(spawnTimer)
			window.clearInterval(msgTimer)
			window.removeEventListener("resize", resize)
			activeItems.forEach((it) => it.el.remove())
			stageEl.querySelectorAll(`.${s.chip}`).forEach((n) => n.remove())
		}
	}, [loading, direction])

	return { stage, canvas, live }
}
